import { useEffect, useState } from "react";

import { Link } from "react-router-dom";

import { Button, Table, TextField, Grid } from "@mui/material";

import { floatVal, intVal, Nullable } from "@jamesgmarks/utilities";
import { Credits } from "@llws/typeorm-entities";

import '../MonthlySubscriptionBreakdown.css';

import { ClientDropdown } from "../../../../../parts/ClientDropdown";
import { Credit, CreditApplied } from "../../../../../../../../entities/hydra";
import { DownloadCSV } from "../../../../../parts/DownloadCSV";
import { ECreditAppliedAs } from "../../../../../../interfaces/ICreditApplied";
import { ECreditTypes } from "../../../../../../interfaces/ICredit";
import { EHydraCreditMethod } from "../../../../../../interfaces/EHydraCreditMethod";
import { HashOf } from "@jamesgmarks/utilities";
import { ICreditBreakdown } from "../../../../../../redux/features/reporting/IMonthlySummaryData";
import { Paper, TableBody, TableContainer, TableFooter, TableHead } from "@mui/material";
import { RequirePermission } from "../../../../../parts/RequirePermission";
import { StyledTableCell, StyledTableRow } from "../../../../../parts/mui/StyledTables";
import { toDollarAmount } from "../../../../../../app-utils";

type TCreditBreakdownRow = Credit | Credits | CreditApplied;

const safeFloat = (value: unknown) => {
  const parsed = floatVal((value ?? '0') as string | number);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isCreditNoteAllocation = (credit: TCreditBreakdownRow): credit is CreditApplied => (
  'appliedAs' in credit && 'dateApplied' in credit
);

const isHydraCredit = (credit: TCreditBreakdownRow): credit is Credit => (
  !isCreditNoteAllocation(credit)
  && (credit as Credit).creditMethod !== undefined
);

const isLegacyCredit = (credit: TCreditBreakdownRow): credit is Credits => (
  !isCreditNoteAllocation(credit)
  && !isHydraCredit(credit)
);
    
const getHydraCreditType = (credit: Credit) => {
  const standaloneCreditAppliedTypesMap = {
    [ECreditAppliedAs.payment]: ECreditTypes.payment,
    [ECreditAppliedAs.reduction]: ECreditTypes.reduction,
  };
      
  return credit.creditMethod === EHydraCreditMethod.creditNote
    ? ECreditTypes.note
    : standaloneCreditAppliedTypesMap[credit.creditsApplied[0].appliedAs];
};
 
const getLegacyCreditLabel = (credit: Credits) => (
  `Credit${credit?.partner?.name?`: ${credit?.partner?.name}`: ''} #${credit.creditNumber}`
);
    
const getHydraCreditLabel = (credit: Credit) => 
  `${getHydraCreditType(credit)}\
    ${credit?.partner?.partnerName ? `: ${credit?.partner?.partnerName}` : ''} #${credit.creditNumber}`;

const getAllocationPartnerLabel = (allocation: CreditApplied) => {
  const partnerNames = [
    ...new Set((allocation.creditNote?.credits ?? [])
      .map((credit) => credit.partner?.partnerName)
      .filter((partnerName) => !!partnerName)),
  ] as string[];

  if (partnerNames.length === 0) {
    return '';
  }

  return partnerNames.length === 1 ? `: ${partnerNames[0]}` : ': Multiple Partners';
};

const getAllocationLabel = (allocation: CreditApplied) => (
  `${ECreditTypes.note}${getAllocationPartnerLabel(allocation)} #${allocation.creditNote?.noteNumber ?? ''}`
);

// Legacy `Credits` rows have no stored `taxAmount` column, so tax must be derived from
// `taxRate * amount`. Hydra `Credit`/`CreditApplied` rows do persist a posted, cents-rounded
// `taxAmount` (see `getCreditNotePostedAmounts`) which can diverge from a raw `taxRate * amount`
// recomputation, so those rows must use the stored value instead of recomputing it.
const getTaxTotal = (credit: TCreditBreakdownRow) => (
  isLegacyCredit(credit)
    ? safeFloat(credit.taxRate) * safeFloat(credit.amount)
    : safeFloat(credit.taxAmount)
);

const getPartnerClawback = (credit: TCreditBreakdownRow) => {
  if (!isCreditNoteAllocation(credit)) {
    return safeFloat(credit.partnerClawback);
  }

  const noteSubtotal = safeFloat(credit.creditNote?.subtotalAmount);
  const allocationAmount = safeFloat(credit.amount);
  const proportion = noteSubtotal > 0 ? allocationAmount / noteSubtotal : 1;

  const totalClawback = (credit.creditNote?.credits ?? [])
    .reduce((sum, noteCredit) => sum + safeFloat(noteCredit.partnerClawback), 0);

  return totalClawback * proportion;
};

const getRowLabel = (credit: TCreditBreakdownRow) => {
  if (isHydraCredit(credit)) {
    return getHydraCreditLabel(credit);
  }

  if (isLegacyCredit(credit)) {
    return getLegacyCreditLabel(credit);
  }

  return getAllocationLabel(credit);
};

const getRowClientName = (credit: TCreditBreakdownRow) => {
  if (isHydraCredit(credit)) {
    return credit.client.clientName;
  }

  if (isLegacyCredit(credit)) {
    return credit.client.name;
  }

  // For allocations, derive from linked credits' clients
  const clientNames = [
    ...new Set((credit.creditNote?.credits ?? [])
      .map((noteCredit) => noteCredit.client?.clientName)
      .filter((clientName) => !!clientName)),
  ] as string[];

  if (clientNames.length === 0) {
    return 'N/A';
  }

  return clientNames.length === 1 ? clientNames[0] : 'Multiple Clients';
};

const getRowCreditNumber = (credit: TCreditBreakdownRow) => {
  if (isCreditNoteAllocation(credit)) {
    return credit.creditNote?.noteNumber ?? '';
  }

  return credit.creditNumber;
};

const getRowInvoices = (credit: TCreditBreakdownRow) => {
  if (isHydraCredit(credit)) {
    return credit.creditsApplied
      .map((ca) => ({ id: ca.invoice.id, number: ca.invoice.invoiceNumber }));
  }

  if (isLegacyCredit(credit)) {
    return credit.creditsHasInvoices
      .map((chi) => ({ id: chi.invoiceId, number: chi.invoice.invoiceNumber }));
  }

  return [{ id: credit.invoiceId, number: `${credit.invoiceId}` }];
};

interface CreditBreakdownTableRowProps {
  credit: TCreditBreakdownRow
  currencyCode: string
}

const CreditBreakdownTableRow = ({ credit, currencyCode }: CreditBreakdownTableRowProps) => {
  const [ linkColor, setLinkColor ] = useState('#007bff');
  
  const [ selected, setSelected ] = useState(false);
  const textColorCss = selected ? { color: 'dodgerblue' } : {};

  const invoices = getRowInvoices(credit);
  const creditNumber = getRowCreditNumber(credit);
  const label = getRowLabel(credit);
  const description = isCreditNoteAllocation(credit)
    ? (credit.description ?? 'Credit note allocation')
    : credit.description;
  const clientName = getRowClientName(credit);
  const partnerClawback = getPartnerClawback(credit);
  const subtotal = safeFloat(credit.amount);
  const lwsClawback = subtotal - partnerClawback;
  const taxTotal = getTaxTotal(credit);
  const total = subtotal + taxTotal;

  return (
    <StyledTableRow
      onClick={() => setSelected((old) => !old)}
      onMouseEnter={() => setLinkColor('white')}
      onMouseLeave={() => setLinkColor('#007bff')}
      customSx={ selected
        ? {
          borderTop: '3px solid dodgerblue',
          borderBottom: '3px solid dodgerblue',
        } 
        : {}
      }
      dark={true}
      hover={true}
    >
      <StyledTableCell dark={true}>
        <ul
          style={
            {
              listStyleType: 'none',
              margin: '0px',
              padding: '0px',
            }
          }
        >
          {
            invoices.map(({ id, number }, i) => (
              <li
                style={
                  {
                    listStyleType: 'none',
                    margin: '0px',
                    padding: '0px',
                  }
                }
                key={i}
              >
                <Link
                  to={`/invoices/${id}`}
                  target="_blank"
                  style={{ color: linkColor }}
                >
                  {number}
                </Link>
              </li>
            ))
          }
        </ul>
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>{creditNumber}</StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        <b>H: </b>
        <span style={{color: 'darkgray' }}>{label.split(':')[0]}</span>
        {label.includes(':') ? `: ${label.split(':').slice(1).join(':')}` : ''}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>{description}</StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>{clientName}</StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {toDollarAmount(-1 * partnerClawback, 'brackets')}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {toDollarAmount(-1 * lwsClawback, 'brackets')}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {toDollarAmount(-1 * subtotal, 'brackets')}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {toDollarAmount(-1 * taxTotal, 'brackets')}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {toDollarAmount(-1 * total, 'brackets')}
      </StyledTableCell>

      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {(isHydraCredit(credit) || isCreditNoteAllocation(credit)) ? credit.currencyCode : currencyCode}
      </StyledTableCell>
      
      <StyledTableCell style={{ ...textColorCss }} dark={true}>
        {isHydraCredit(credit) && credit.creditNote?.noteNumber}
        {isCreditNoteAllocation(credit) && credit.creditNote?.noteNumber}
      </StyledTableCell>
    </StyledTableRow>
  );
};

interface CreditsBreakdownTableProps {
  creditBreakdown: ICreditBreakdown
  currencyCode: string
}

export const CreditsBreakdownTable = ({
  creditBreakdown: { legacyCredits, hydraCredits, creditNoteAllocations },
  currencyCode,
}: CreditsBreakdownTableProps) => {  
  const [ filteredLegacyCredits, setFilteredLegacyCredits ] = useState(legacyCredits);
  const [ filteredHydraCredits, setFilteredHydraCredits ] = useState(hydraCredits);
  const [ filteredCreditNoteAllocations, setFilteredCreditNoteAllocations ] = useState(creditNoteAllocations);
  
  const [sortCriteria, setSortCriteria] = useState(['clientName', 'asc']);
  
  const [ totals, setTotals ] = useState({
    partnerRevenue: 0,
    lwsRevenue: 0,
    subTotal: 0,
    taxes: 0,
    total: 0,
  });
  
  const [ searchClientId, setSearchClientId ] = useState(null as Nullable<number>);

  useEffect(() => {
    const newTotals = [ ...filteredLegacyCredits, ...filteredHydraCredits, ...filteredCreditNoteAllocations ]
      .reduce((acc, cur) => {
        const subtotal = safeFloat(cur.amount);
        const partnerClawback = getPartnerClawback(cur);
        const taxTotal = getTaxTotal(cur);

        return {
          partnerRevenue: (acc.partnerRevenue ?? 0) - partnerClawback,
          lwsRevenue: acc.lwsRevenue - (subtotal - partnerClawback),
          subTotal: acc.subTotal - subtotal,
          taxes: acc.taxes - taxTotal,
          total: acc.total - subtotal - taxTotal,
        };
      }, {
        partnerRevenue: 0,
        lwsRevenue: 0,
        subTotal: 0,
        taxes: 0,
        total: 0,
      });

    setTotals(newTotals);
  }, [ filteredLegacyCredits, filteredHydraCredits, filteredCreditNoteAllocations ]);

  const getCreditLabelForSort = (credit: TCreditBreakdownRow) =>
    getRowLabel(credit).split(': ')[1]?.toLowerCase() ?? getRowLabel(credit).toLowerCase();

  const getClientNameForSort = (credit: TCreditBreakdownRow) =>
    getRowClientName(credit).toLowerCase();

  const getLwsShareForSort = (credit: TCreditBreakdownRow) => (
    safeFloat(credit.amount) - getPartnerClawback(credit)
  );

  const getTaxTotalForSort = (credit: TCreditBreakdownRow) => getTaxTotal(credit);

  const getTotalForSort = (credit: TCreditBreakdownRow) => safeFloat(credit.amount) + getTaxTotalForSort(credit);

  const sortFns = {
    'creditNumber': (a, b) => getRowCreditNumber(a) > getRowCreditNumber(b) ? 1 : -1,
    'itemName': (a, b) => (getCreditLabelForSort(a) > getCreditLabelForSort(b)) ? 1 : -1,
    'clientName': (a, b) => (getClientNameForSort(a) > getClientNameForSort(b)) ? 1 : -1,
    'partnerShare': (a, b) => getPartnerClawback(a) > getPartnerClawback(b) ? 1 : -1,
    'lwsShare': (a, b) => (getLwsShareForSort(a) > getLwsShareForSort(b) ? 1 : -1),
    'subtotal': (a, b) => (safeFloat(a.amount) > safeFloat(b.amount) ? 1 : -1),
    'taxTotal': (a, b) => (getTaxTotalForSort(a) > getTaxTotalForSort(b) ? 1 : -1),
    'total': (a, b) => (getTotalForSort(a) > getTotalForSort(b) ? 1 : -1),
    'noteNumber': (a, b) => (
      (isHydraCredit(a) || isCreditNoteAllocation(a)) && (isHydraCredit(b) || isCreditNoteAllocation(b)))
      ? ((a.creditNote?.noteNumber ?? '') > (b.creditNote?.noteNumber ?? '') ? 1 : -1)
      : 0,
  } as HashOf<(a: TCreditBreakdownRow, b: TCreditBreakdownRow) => (1 | -1)>;

  const sortBy = (criteria: string) => {
    setSortCriteria([criteria, (sortCriteria[0] === criteria && sortCriteria[1] === 'asc') ? 'desc' : 'asc']);
  };

  const getSortIcon = (criteria: string) => {
    return sortCriteria[0] === criteria ? (sortCriteria[1] === 'asc' ? <>&#x25b2;</> : <>&#x25bc;</>) : null;
  };

  const doSort = (a: TCreditBreakdownRow, b: TCreditBreakdownRow) => {
    return (
      sortFns[sortCriteria[0]](a, b) * (sortCriteria[1] === 'asc' ? 1 : -1)
    );
  };

  const initialFilters = {
    invoiceNumber: '',
    creditNumber: '',
    itemName: '',
    clientName: '',
  };

  const [ filters, setFilters ] = useState(initialFilters);

  const updateFilters = (filterName: string, value: string) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      [filterName]: value,
    }));
  };

  const applyFilters = () => {
    setFilteredLegacyCredits(
      legacyCredits.filter(creditItem => {
        return (
          !filters.clientName
          || creditItem.client.name.toLowerCase().includes(filters.clientName.toLowerCase())
        )
      
        && (
          !filters.itemName
          || getLegacyCreditLabel(creditItem).toLowerCase().includes(filters.itemName.toLowerCase())
        )

        && (!filters.invoiceNumber || creditItem.creditsHasInvoices.filter(
          (chi) => chi.invoice.invoiceNumber.includes(filters.invoiceNumber),
        ).length > 0)

        && (!filters.creditNumber || creditItem.creditNumber.includes(filters.creditNumber));
      }),
    );

    setFilteredHydraCredits(
      hydraCredits
        .filter(creditItem => {
          return (
            !filters.clientName
            || creditItem.client.clientName.toLowerCase().includes(filters.clientName.toLowerCase())
          )

          && (
            !filters.itemName
            || getHydraCreditLabel(creditItem).toLowerCase().includes(filters.itemName.toLowerCase())
          )

          && (
            creditItem.creditNoteId // Credit note credits generate `Payment`s, not `CreditApplied` records
            || !filters.invoiceNumber
            || creditItem.creditsApplied
              .filter((ca) => ca.invoice.invoiceNumber.includes(filters.invoiceNumber)).length > 0
          )

          && (!filters.creditNumber || creditItem.creditNumber.includes(filters.creditNumber));
        }),
    );

    setFilteredCreditNoteAllocations(
      creditNoteAllocations.filter((allocation) => (
        (!filters.clientName || getRowClientName(allocation).toLowerCase().includes(filters.clientName.toLowerCase()))
        &&
        (!filters.itemName || getAllocationLabel(allocation).toLowerCase().includes(filters.itemName.toLowerCase()))
        && (!filters.invoiceNumber || `${allocation.invoiceId}`.includes(filters.invoiceNumber))
        && (!filters.creditNumber || (allocation.creditNote?.noteNumber ?? '').includes(filters.creditNumber))
      )),
    );
  };

  return (
    <>
      <Grid container mt={5}>
        <Grid item textAlign='left' xs={12}>
          <TextField
            label='Invoice Number'
            value={filters.invoiceNumber}
            onChange={
              (e) => updateFilters('invoiceNumber', e.target.value.trim())
            }
            size='small'
            sx={{ width: '25%' }}
            variant='outlined'
          />
        </Grid>

        <Grid item mt={1.5} textAlign='left' xs={12}>
          <TextField
            label='Credit Number'
            value={filters.creditNumber}
            onChange={
              (e) => updateFilters('creditNumber', e.target.value.trim())
            }
            size='small'
            sx={{ width: '25%' }}
            variant='outlined'
          />
        </Grid>

        <Grid item mt={1.5} textAlign='left' xs={12}>
          <TextField
            label='Item'
            value={filters.itemName}
            onChange={
              (e) => updateFilters('itemName', e.target.value.trim())
            }
            size='small'
            sx={{ width: '50%' }}
            variant='outlined'
          />
        </Grid>

        <Grid item mt={1.5} xs={6}>
          <ClientDropdown
            allowFreeform
            clientId={searchClientId}
            onClientChanged={(client) => {
              setSearchClientId(client ? intVal(client.id) : null);
            }}
            onClientFreeForm={(clientString) => {
              clientString !== null && updateFilters('clientName', clientString);
            }}
          />
        </Grid>

        <Grid item textAlign='left' mt={1} xs={12}>
          <Button
            onClick={applyFilters}
            size='small'
            variant='contained'
          >
              Filter
          </Button>
          <Button
            sx={{ ml: 2 }}
            color='info'
            onClick={() => {
              setFilters(initialFilters);
              setFilteredHydraCredits(hydraCredits);
              setFilteredLegacyCredits(legacyCredits);
              setFilteredCreditNoteAllocations(creditNoteAllocations);
            }}
            size='small'
            variant='contained'
          >
              Clear
          </Button>
        </Grid>
      </Grid>

      <Grid textAlign='left' sx={{ color: 'gray', position: 'relative', top: 45, left: 8 }}>
        <small>Click any row to toggle selection/highlight...</small>
      </Grid>
      <DownloadCSV filename={`credits-breakdown`}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <StyledTableRow dark={true}>
                <StyledTableCell dark={true}>Invoice(s)</StyledTableCell>

                <StyledTableCell onClick={() => sortBy('creditNumber')} dark={true} className='sortable-header'>
                  Credit # {getSortIcon('creditNumber')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('itemName')} dark={true} className="sortable-header">
                  Item {getSortIcon('itemName')}
                </StyledTableCell>
                
                <StyledTableCell dark={true}>
                  Description
                </StyledTableCell>
                
                <StyledTableCell onClick={() => sortBy('clientName')} dark={true} className="sortable-header">
                  Client {getSortIcon('clientName')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('partnerShare')} dark={true} className="sortable-header">
                  Partner Share {getSortIcon('partnerShare')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('lwsShare')} dark={true} className="sortable-header">
                  LWS Share {getSortIcon('lwsShare')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('subtotal')} dark={true} className="sortable-header">
                  Subtotal {getSortIcon('subtotal')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('taxTotal')} dark={true} className="sortable-header">
                  Tax Total {getSortIcon('taxTotal')}
                </StyledTableCell>

                <StyledTableCell onClick={() => sortBy('total')} dark={true} className="sortable-header">
                  Total {getSortIcon('total')}
                </StyledTableCell>
                
                <StyledTableCell dark={true}>Currency </StyledTableCell>
                <StyledTableCell onClick={() => sortBy('noteNumber')} dark={true} className='sortable-header'>
                  Credit Note # {getSortIcon('noteNumber')}
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {
                [ ...filteredLegacyCredits, ...filteredHydraCredits, ...filteredCreditNoteAllocations ]
                  .sort((a, b) => doSort(a, b))
                  .map((credit, i) => 
                    <CreditBreakdownTableRow credit={credit} currencyCode={currencyCode} key={i} />)
              }

            </TableBody>
            <RequirePermission grant="FULL_ACCESS">
              <TableFooter>
                <StyledTableRow dark={true}>
                  <StyledTableCell dark={true}></StyledTableCell>
                  <StyledTableCell dark={true}>Totals</StyledTableCell>
                  <StyledTableCell dark={true}></StyledTableCell>
                  <StyledTableCell dark={true}></StyledTableCell>
                  <StyledTableCell dark={true}></StyledTableCell>
                  <StyledTableCell dark={true}>
                    {`${toDollarAmount(totals.partnerRevenue, 'brackets')}`}
                  </StyledTableCell>
                  <StyledTableCell dark={true}>{`${toDollarAmount(totals.lwsRevenue, 'brackets')}`}</StyledTableCell>
                  <StyledTableCell dark={true}>{`${toDollarAmount(totals.subTotal, 'brackets')}`}</StyledTableCell>
                  <StyledTableCell dark={true}>{`${toDollarAmount(totals.taxes, 'brackets')}`}</StyledTableCell>
                  <StyledTableCell dark={true}>{`${toDollarAmount(totals.total, 'brackets')}`}</StyledTableCell>
                  <StyledTableCell dark={true}></StyledTableCell>
                  <StyledTableCell dark={true}></StyledTableCell>
                </StyledTableRow>
              </TableFooter>
            </RequirePermission>
          </Table>
        </TableContainer>
      </DownloadCSV>
    </>
  );
};
